import React, { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface Conversation {
  id: string;
  title: string;
  updated_at: string;
}

interface ChatConversationsListProps {
  projectId: string;
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
}

export const ChatConversationsList: React.FC<ChatConversationsListProps> = ({
  projectId,
  selectedConversationId,
  onSelectConversation,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchConversations = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_conversations")
        .select("id, title, updated_at")
        .eq("project_id", projectId)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();

    const channel = supabase
      .channel("conversations-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "chat_conversations",
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  const deleteConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from("chat_conversations")
        .delete()
        .eq("id", conversationId);

      if (error) throw error;
      setConversations(conversations.filter(c => c.id !== conversationId));
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return null;
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-1 border-t border-border pt-4">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className="group flex items-center justify-between py-3 px-1 cursor-pointer hover:bg-accent/30 rounded-lg transition-colors border-b border-border/50 last:border-0"
            onClick={() => onSelectConversation(conversation.id)}
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{conversation.title}</p>
              <p className="text-xs text-muted-foreground">
                Last message {formatDistanceToNow(new Date(conversation.updated_at), { addSuffix: false })} ago
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              onClick={(e) => deleteConversation(e, conversation.id)}
            >
              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};